import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { track } from "@/lib/track";
import { useAuth } from "@/context/AuthContext";
import { Button, Spinner } from "@/components/ui-kit";
import { APP } from "@/config";
import { CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

const MAX_POLLS = 10;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [plan, setPlan] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let active = true;
    const tick = async () => {
      if (!active) return;
      polls.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPlan(data.plan_label);
          setState("paid");
          track("subscription_completed", { session_id: sessionId });
          try { const me = await api.get("/auth/me"); setUser(me.data.user); } catch { /* noop */ }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* keep polling */ }
      if (polls.current >= MAX_POLLS) { setState("timeout"); return; }
      setTimeout(tick, 2000);
    };
    tick();
    return () => { active = false; };
  }, [sessionId]);

  return (
    <div className="band-3-5 min-h-screen bg-[var(--ns-bg)] grid place-items-center px-5">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-[var(--ns-surface)] rounded-[var(--ns-radius)] border border-[var(--ns-border)] p-8 text-center ns-card" data-testid="payment-success-page">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-9 h-9 rounded-xl bg-[var(--ns-primary)] text-white grid place-items-center font-head font-bold">N</div>
          <span className="font-head text-lg font-bold">{APP.name}</span>
        </div>
        {state === "polling" && <Spinner label="Confirming your payment" />}
        {state === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-[#2A8555]" />
            <h1 className="font-head text-2xl font-bold mt-3">You're subscribed! 🎉</h1>
            <p className="text-[var(--ns-muted)] mt-2">Your {plan || "plan"} is active. Thank you for supporting your child's math journey.</p>
            <Button className="mt-6 mx-auto px-7" data-testid="payment-continue-btn" onClick={() => nav("/parent")}>
              Go to dashboard <ArrowRight size={18} />
            </Button>
          </>
        )}
        {state === "timeout" && (
          <>
            <Spinner label="Still processing" />
            <p className="text-[var(--ns-muted)] mt-2 text-sm">Your payment is taking a moment to confirm. It's safe to head to your dashboard — we'll update it automatically.</p>
            <Button variant="outline" className="mt-5 mx-auto" onClick={() => nav("/parent")}>Go to dashboard</Button>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle size={48} className="mx-auto text-[#B23A28]" />
            <h1 className="font-head text-2xl font-bold mt-3">We couldn't confirm this payment</h1>
            <p className="text-[var(--ns-muted)] mt-2">No charge may have gone through. You can try again from your dashboard.</p>
            <Button variant="outline" className="mt-6 mx-auto" onClick={() => nav("/parent")}>Back to dashboard</Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
